import { useState, useEffect } from 'react';
import {
  Autocomplete,
  TextField,
  CircularProgress,
} from '@mui/material';
import { novaPoshtaReference, type ContactPersonResult } from '../../../services/novaPoshtaClient';

interface NovaPoshtaContactPersonSelectProps {
  apiKey: string | null;
  value: ContactPersonResult | null;
  onChange: (value: ContactPersonResult | null) => void;
  label: string;
  disabled?: boolean;
}

// A Counterparty (Sender) can have several ContactPersons — different employees, each
// with their own phone (the one a courier calls). Fetched once per apiKey, then filtered
// client-side, same pattern as the warehouse select — a company has at most a handful.
export default function NovaPoshtaContactPersonSelect({
  apiKey,
  value,
  onChange,
  label,
  disabled,
}: NovaPoshtaContactPersonSelectProps) {
  const [options, setOptions] = useState<ContactPersonResult[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!apiKey) {
      setOptions([]);
      return;
    }
    let cancelled = false;

    setLoading(true);
    setError(null);
    novaPoshtaReference.getSenderCounterpartyRef(apiKey)
      .then((senderRef) => novaPoshtaReference.getContactPersons(apiKey, senderRef))
      .then((persons) => {
        if (cancelled) {
          return;
        }
        setOptions(persons);
      })
      .catch((err) => {
        if (cancelled) {
          return;
        }
        console.error('Error loading contact persons:', err);
        setError('Не вдалося завантажити контактних осіб');
      })
      .finally(() => {
        if (!cancelled) {
          setLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [apiKey]);

  return (
    <Autocomplete
      options={options}
      value={value}
      onChange={(_, newValue) => onChange(newValue)}
      getOptionLabel={(option) => `${option.name}${option.phone ? ` — ${option.phone}` : ''}`}
      isOptionEqualToValue={(option, val) => option.ref === val.ref}
      loading={loading}
      disabled={disabled || !apiKey}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          size="small"
          fullWidth
          error={!!error}
          helperText={error}
          slotProps={{
            input: {
              ...params.InputProps,
              endAdornment: (
                <>
                  {loading && <CircularProgress size={16} />}
                  {params.InputProps.endAdornment}
                </>
              ),
            },
          }}
        />
      )}
    />
  );
}
